import { Helmet } from "react-helmet-async";
import { useEffect, useState, useCallback } from "react";
import {
  Card,
  Stack,
  Container,
  Typography,
  CircularProgress,
  Box,
  Grid,
  FormControl,
  TextField,
  Button
} from "@mui/material";

import axios from "axios";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import { useSearchParams } from "react-router-dom";

export default function EditBracketSettingPage() {
  const [searchParams] = useSearchParams();
  const [competition, setCompetition] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [disabled, setDisabled] = useState(false);

  const [bracketId, setBracketId] = useState(false);
  const [allowMultipleBracket, setAllowMultipleBracket] = useState(false);
  const [openSelectionsUtc, setOpenSelectionsUtc] = useState();
  const [closeSelectionsUtc, setCloseSelectionsUtc] = useState();
  const [tiebreakerAnswer, setTiebreakerAnswer] = useState();
  const [tiebreakerQuestion, setTiebreakerQuestion] = useState();

  useEffect(() => {
    if (searchParams.has("type")) {
      const competition = searchParams.get("type");
      setCompetition(competition);
    }
  }, [searchParams]);

  const getData = (competition) => {
    setLoading(true);
    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/admin/bracket-setting?competition=${competition}`)
      .then(function (response) {
        const data = response?.data;
        setLoading(false);
        setBracketId(data.bracketId)
        setAllowMultipleBracket(data?.allowMultipleBracket || false);
        setOpenSelectionsUtc(data?.openSelectionsUtc);
        setCloseSelectionsUtc(data?.closeSelectionsUtc);
        setTiebreakerAnswer(data.tiebreakerAnswer);
        setTiebreakerQuestion(data.tiebreakerQuestion);
      });
  };

  const fn = useCallback(() => getData(competition), [competition])
  useEffect(() => {
    if(competition) fn(competition)
  }, [competition, fn])

  useEffect(() => {
    if(!!openSelectionsUtc && !!closeSelectionsUtc) {
      setError(moment(openSelectionsUtc).isAfter(closeSelectionsUtc));
    }
  }, [openSelectionsUtc, closeSelectionsUtc])

  const handleSubmit = () => {
    const parsedTiebreakerAnswer = Number(tiebreakerAnswer);

    console.log("submit clicked", {
      bracketId,
      openSelectionsUtc,
      closeSelectionsUtc,
      tiebreakerAnswer: parsedTiebreakerAnswer,
      competition,
    });

    if (!bracketId) {
      console.log("No bracketId");
      return;
    }

    if (!openSelectionsUtc) {
      console.log("No openSelectionsUtc");
      return;
    }

    if (!closeSelectionsUtc) {
      console.log("No closeSelectionsUtc");
      return;
    }

    if (!Number.isFinite(parsedTiebreakerAnswer)) {
      console.log("Invalid tiebreakerAnswer");
      return;
    }

    setLoading(true);

    axios
      .put(`${process.env.REACT_APP_API_BASE_URL}/admin/bracket-setting`, {
        bracketId,
        allowMultipleBracket,
        openSelectionsUtc,
        closeSelectionsUtc,
        tiebreakerAnswer: parsedTiebreakerAnswer,
        tiebreakerQuestion,
        competition,
      })
      .then((res) => {
        console.log("PUT success", res.data);
        setDisabled(true);
      })
      .catch((err) => {
        console.error("PUT failed", err?.response?.data || err);
      })
      .finally(() => setLoading(false));
  };

  return (
    <>
      <Helmet>
        <title> Bracket Setting </title>
      </Helmet>

      <Container>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={5}
        >
          <Typography variant="h4" gutterBottom>
            Bracket Setting
          </Typography>
        </Stack>

        <Card style={{ padding: 20 }}>
          {loading ? (
            <Box
              sx={{ display: "flex", justifyContent: "center" }}
              style={{ padding: 20 }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={3}>
              <Grid item xs={12} sm={12} md={12}>

                <FormControl sx={{ m: 1, minWidth: 620 }} size="small">
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DateTimePicker
                      label="Open Selections Utc"
                      value={openSelectionsUtc}
                      onChange={(newValue) => setOpenSelectionsUtc(newValue)}
                      renderInput={(params) => (
                        <TextField 
                          {...params} 
                          error={error} 
                          helperText="The open date cannot be later than the close date" 
                        />
                      )}
                      disabled={disabled}
                    />
                  </LocalizationProvider>
                </FormControl>
                
                <FormControl sx={{ m: 1, minWidth: 620 }} size="small">
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DateTimePicker
                      label="Close Selections Utc"
                      value={closeSelectionsUtc}
                      onChange={(newValue) => setCloseSelectionsUtc(newValue)}
                      renderInput={(params) => <TextField {...params} />}
                      disabled={disabled}
                    />
                  </LocalizationProvider>
                </FormControl>

                <FormControl sx={{ m: 1, minWidth: 620 }} size="small">
                  <TextField
                    required
                    label="Tiebreaker Question"
                    placeholder="Tiebreaker Question"
                    name={'tiebreakerQuestion'}
                    value={tiebreakerQuestion || ""}
                    onChange={(e) => setTiebreakerQuestion(e.target.value)}
                    variant="outlined"
                    disabled={disabled}
                  />
                </FormControl>

                <FormControl sx={{ m: 1, minWidth: 620 }} size="small">
                    <TextField
                      required
                      type="number"
                      label="Right Answer on Tiebreaker"
                      placeholder="Right Answer on Tiebreaker"
                      name={'right_answer'}
                      value={tiebreakerAnswer ?? ""}
                      onChange={(e) => setTiebreakerAnswer(e.target.value)}
                      inputProps={{ step: 1 }}
                      variant="outlined"
                      disabled={disabled}
                    />
                  </FormControl>
              </Grid>
              <Grid item xs={12} sm={12} md={12} >
                {
                  disabled && (
                    <Button
                      variant="contained"
                      onClick={() => setDisabled(false)}
                    >
                      Edit
                    </Button>
                  )
                }
                {
                  !disabled && (
                    <>
                      <Button
                        variant="contained"
                        onClick={() => handleSubmit()}
                        disabled={!!error}
                      >
                        Update
                      </Button>
                      <Button onClick={() => setDisabled(true)}>Cancel</Button>
                    </>
                  )
                }
              </Grid>
            </Grid>
          )}
        </Card>
      </Container>
    </>
  );
}
